import React from "react";
import PropTypes from "prop-types";
import { Redirect } from "react-router";
import * as R from "ramda";
import { compose, withApollo } from "react-apollo";

import { Alert } from "antd";
import LoginForm from "../component/LoginForm";

import { authenticate, withBasicAuth, withRefreshToken } from "../api/apollo";
import WithAuth from "../container/WithAuth";
import LoginDefault from "./Login.Default";

const parseSearch = R.pipe(
    R.tail,
    R.split("&"),
    R.map(R.split("=")),
    R.fromPairs
);

class Login extends React.Component {
    mounted = true;
    state = {
        isLoading: false,
        errors: null,
    };

    handleAuth = async strategy => {
        if (this.mounted) {
            this.setState({ isLoading: true });
        }

        try {
            await authenticate(this.props.client)(strategy);
        } catch (e) {
            if (e.graphQLErrors) {
                throw e;
            }

            if (this.mounted) {
                this.setState({ errors: e });
            }
        }

        if (this.mounted) {
            this.setState({ isLoading: false });
        }
    };

    handleLogin = ({ username, password, token }) => {
        this.handleAuth(withBasicAuth(username, password, token));
    };

    getParamInSearch = paramName => {
        const search = R.pathOr("", ["location", "search"], this.props);
        const parsedSearch = parseSearch(search);
        return parsedSearch[paramName];
    };

    componentDidMount() {
        const token = this.getParamInSearch("token");
        if (token) {
            this.handleAuth(withRefreshToken(token));
        }
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    render() {
        if (this.props.isAuthenticated) {
            const nextParam = this.getParamInSearch("next");
            let location;
            if (nextParam) {
                location = nextParam;
            } else {
                // Remove this block once fully cognito enabled.
                const defaultPath = "/";
                const prevLocation = R.pathOr(
                    defaultPath,
                    ["location", "state", "from", "pathname"],
                    this.props
                );
                location = prevLocation.includes("logout")
                    ? defaultPath
                    : prevLocation;
            }
            return <Redirect to={location} />;
        }

        const PageLayout = this.props.config.layout;

        const title = this.props.isRequestPortal
            ? "Welcome to the Request Portal"
            : undefined;
        const d3Text = this.props.isRequestPortal
            ? "Single Sign On"
            : undefined;

        return (
            <PageLayout>
                <LoginForm
                    isLoading={this.state.isLoading}
                    onSubmit={this.handleLogin}
                    title={title}
                    d3Text={d3Text}
                    stackButtons={this.props.isRequestPortal}
                    showPoweredBy={this.props.isRequestPortal}
                    config={this.props.config}
                >
                    {this.state.errors && (
                        <Alert
                            message="Failed to login"
                            description={this.props.config.error}
                            type="error"
                            showIcon
                        />
                    )}
                </LoginForm>
            </PageLayout>
        );
    }
}

Login.propTypes = {
    errors: PropTypes.object,
    config: PropTypes.shape({
        layout: PropTypes.func,
        token: PropTypes.bool,
        error: PropTypes.string,
    }),
    isAuthenticated: PropTypes.bool,
    isRequestPortal: PropTypes.bool,
};

Login.defaultProps = {
    isRequestPortal: false,
    config: {
        layout: LoginDefault,
        token: false,
        error: "Invalid username or password. Please try again.",
    },
};

export default compose(withApollo, WithAuth)(Login);
